"use client";

import { authService, type AuthResponse, type ProfileUpdateRequest, type User } from "@/services/api";
import { EVENT_TYPES } from "@/utils/constants";
import { logEvent } from "@/utils/helpers/analyticsHelpers";
import React, { createContext, useContext, useEffect, useState } from "react";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<AuthResponse>;
  register: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    newPassword: string;
    confirmPassword: string;
    instagram_username?: string;
    marketingOptIn?: boolean;
  }) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<AuthResponse>;
  resetPassword: (password: string, confirmPassword: string, token: string) => Promise<AuthResponse>;
  googleAuth: (credential: string) => Promise<AuthResponse>;
  updateUser: (userData: ProfileUpdateRequest) => Promise<AuthResponse>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      setIsLoading(true);

      if (typeof window === "undefined") {
        return;
      }

      authService.initializeAuth();
      const storedUser = authService.getStoredUser();
      setUser(storedUser || null);
    } catch (error) {
      console.error("Auth initialization error:", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, remember?: boolean): Promise<AuthResponse> => {
    try {
      const response = await authService.login({ email, password, remember });
      if (response.success && response.data) {
        setUser(response.data);
        logEvent(response.data, EVENT_TYPES.LOGIN);
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Login failed",
      };
    }
  };

  const register = async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    newPassword: string;
    confirmPassword: string;
    instagram_username?: string;
    marketingOptIn?: boolean;
  }): Promise<AuthResponse> => {
    try {
      const response = await authService.register(userData);
      if (response.success && response.data) {
        setUser(response.data);
        logEvent(response.data, EVENT_TYPES.REGISTER);
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Registration failed",
      };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (user) {
        logEvent(user, EVENT_TYPES.LOGOUT);
      }
      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
    }
  };

  const forgotPassword = async (email: string): Promise<AuthResponse> => {
    return authService.forgotPassword({ email });
  };

  const resetPassword = async (
    password: string,
    confirmPassword: string,
    token: string
  ): Promise<AuthResponse> => {
    return authService.resetPassword({ password, confirmPassword }, token);
  };

  const googleAuth = async (credential: string): Promise<AuthResponse> => {
    try {
      const response = await authService.googleAuth(credential);
      if (response.success && response.data) {
        setUser(response.data);
        logEvent(response.data, EVENT_TYPES.LOGIN, { auth_method: "google" });
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Google authentication failed",
      };
    }
  };

  const updateUser = async (userData: ProfileUpdateRequest): Promise<AuthResponse> => {
    try {
      const response = await authService.updateProfile(userData);
      if (response.success && response.data) {
        setUser(response.data);
        logEvent(response.data, EVENT_TYPES.UPDATE_USER);
      }
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update user",
      };
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    googleAuth,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
